import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { offerTiming } from '../../offerTiming';
const useStyles = makeStyles()(() => ({
    container: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 999
    },
    toggleButton: {
        alignItems: 'center',
        backgroundColor: '#4a9eff',
        border: 'none',
        borderRadius: '50%',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '16px',
        height: '50px',
        justifyContent: 'center',
        position: 'relative',
        width: '50px',
        '&:hover': {
            backgroundColor: '#3d8bef'
        },
        '&:focus-visible': {
            outline: '3px solid #ffffff',
            outlineOffset: '3px'
        }
    },
    badge: {
        position: 'absolute',
        top: '-5px',
        right: '-5px',
        backgroundColor: '#dc3545',
        color: 'white',
        borderRadius: '50%',
        minWidth: '20px',
        height: '20px',
        padding: '0 4px',
        fontSize: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold'
    },
    panel: {
        position: 'absolute',
        bottom: '60px',
        right: '0',
        backgroundColor: '#1a2332',
        border: '2px solid #4a9eff',
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '16px',
        width: 'min(420px, calc(100vw - 40px))',
        maxHeight: 'min(400px, calc(100vh - 100px))',
        overflowY: 'auto',
        overflowWrap: 'anywhere',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
    },
    header: {
        color: '#ffffff',
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '12px',
        borderBottom: '1px solid #4a9eff',
        paddingBottom: '8px'
    },
    requestItem: {
        backgroundColor: '#2d3748',
        border: '1px solid #4a9eff',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '8px',
        '&:last-child': {
            marginBottom: 0
        }
    },
    requestHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px'
    },
    clientName: {
        color: '#75baff',
        fontWeight: 'bold',
        fontSize: '14px'
    },
    timestamp: {
        color: '#d0d0d0',
        fontSize: '12px',
        fontWeight: 600
    },
    expired: {
        color: '#ffb4ab'
    },
    language: {
        color: '#ffd54f',
        fontSize: '13px',
        marginBottom: '10px'
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    actionButton: {
        flex: '1 1 88px',
        fontSize: '12px',
        minHeight: '44px',
        padding: '8px'
    },
    acceptButton: {
        backgroundColor: '#28a745',
        '&:hover': {
            backgroundColor: '#218838'
        }
    },
    declineButton: {
        backgroundColor: '#dc3545',
        '&:hover': {
            backgroundColor: '#c82333'
        }
    },
    removeButton: {
        backgroundColor: '#6c757d',
        '&:hover': {
            backgroundColor: '#5a6268'
        }
    },
    error: {
        color: '#ffffff',
        fontSize: '12px',
        margin: '8px 0 0'
    }
}));
const MinimizedRequestList = ({ actionError, actionPending, requests, onAccept, onDecline, onRemove }) => {
    const { classes } = useStyles();
    const [isExpanded, setIsExpanded] = useState(false);
    const [now, setNow] = useState(Date.now());
    const panelId = 'minimized-interpreter-requests';
    const headingId = 'minimized-interpreter-requests-heading';
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    if (requests.length === 0) {
        return null;
    }
    return (React.createElement("div", { className: classes.container },
        React.createElement("button", { "aria-controls": panelId, "aria-expanded": isExpanded, "aria-label": `${requests.length} pending interpreter ${requests.length === 1 ? 'request' : 'requests'}`, className: classes.toggleButton, onClick: () => setIsExpanded(value => !value), title: `${requests.length} pending interpreter ${requests.length === 1 ? 'request' : 'requests'}` },
            React.createElement("span", { "aria-hidden": 'true' }, "\uD83D\uDCCB"),
            React.createElement("span", { className: classes.badge }, requests.length > 9 ? '9+' : requests.length)),
        isExpanded && (React.createElement("section", { "aria-labelledby": headingId, className: classes.panel, id: panelId },
            React.createElement("h2", { className: classes.header, id: headingId },
                "Pending requests (",
                requests.length,
                ")"),
            requests.map(request => {
                const timing = offerTiming(request, now);
                const pendingKind = actionPending?.requestId === request.id ? actionPending.kind : null;
                const disabled = timing.expired || Boolean(actionPending);
                const error = actionError?.requestId === request.id ? actionError.message : '';
                return (React.createElement("article", { "aria-busy": Boolean(pendingKind), className: classes.requestItem, key: request.id },
                    React.createElement("div", { className: classes.requestHeader },
                        React.createElement("span", { className: classes.clientName }, request.clientName),
                        React.createElement("span", { className: `${classes.timestamp} ${timing.expired ? classes.expired : ''}`, role: timing.expired ? 'status' : undefined }, timing.label)),
                    React.createElement("div", { className: classes.language },
                        "Language: ",
                        request.language),
                    request.demoMode && (React.createElement("div", { className: classes.language, role: 'note' }, "Demo call \u2014 not payable")),
                    React.createElement("div", { className: classes.actions },
                        React.createElement(Button, { accessibilityLabel: `Accept request from ${request.clientName}`, className: `${classes.actionButton} ${classes.acceptButton}`, disabled: disabled, onClick: () => onAccept(request.id), label: pendingKind === 'accept' ? 'Accepting…' : timing.expired ? 'Expired' : 'Accept', type: BUTTON_TYPES.PRIMARY }),
                        React.createElement(Button, { accessibilityLabel: `Decline request from ${request.clientName}`, className: `${classes.actionButton} ${classes.declineButton}`, disabled: disabled, onClick: () => onDecline(request.id), label: pendingKind === 'decline' ? 'Declining…' : 'Decline', type: BUTTON_TYPES.SECONDARY }),
                        React.createElement(Button, { accessibilityLabel: `Dismiss request from ${request.clientName}`, className: `${classes.actionButton} ${classes.removeButton}`, disabled: Boolean(pendingKind), onClick: () => onRemove(request.id), label: 'Dismiss', type: BUTTON_TYPES.SECONDARY })),
                    error && React.createElement("p", { className: classes.error, role: 'alert' }, error)));
            })))));
};
export default MinimizedRequestList;
